/*
 * Le guide del gestionale: un dato, non componenti. Le legge la pagina Guide (pages/GuidePage.js) e lo script
 * che rifà le foto (scripts/guide-foto.mjs). Come si aggiunge una guida: docs/GUIDE.md.
 *
 * Una guida:
 *   id       l'indirizzo (/guide/<id>) e la cartella delle foto (public/guide-foto/<id>/)
 *   gruppo   uno dei GRUPPI qui sotto: il lavoro da fare
 *   voce     la voce del menu che serve per farlo (lib/accessi.js): chi non ce l'ha non vede la guida
 *   ruoli    facoltativo: solo questi ruoli (per esempio le cose del titolare)
 *   pubblica facoltativo: si apre anche senza login, da /aiuto/<id>
 *   vai      il pulsante che porta dove si fa la cosa
 *   foto     per lo script: con chi entra (titolare, venditore, amministrazione, montatore, nessuno), da quale
 *            pagina parte, e con `prepara` la ricetta del mondo finto che serve (scripts/guide-ricette.mjs)
 *   passi    ogni passo un'azione: `testo` per chi legge, `parte` per aprire un pezzo nuovo della guida.
 *            Per lo script: `punta` (il punto da premere, che nella foto ha il segno: se non c'è più, lo script si
 *            ferma e nomina guida e passo), `prima` (cosa fare prima della foto), `premi: false` quando il punto si
 *            guarda e non si preme, `gesto` quando non basta un clic, `poi` (cosa deve comparire dopo).
 *            `punta`, `prima`, `gesto` e `poi` possono essere { telefono, computer } quando i due schermi differiscono.
 *            In `prima`: { riempi, con } · { premi } · { allega } · { tasti, su } · { scegli, valore } (una tendina).
 *            Un passo senza `punta` non ha foto: succede fuori dal gestionale (l'email, il telefono).
 */

export const GRUPPI = [
  { id: "primi-passi", titolo: "Primi passi" },
  { id: "clienti", titolo: "Clienti" },
  { id: "visite", titolo: "Visite e calendario" },
  { id: "preventivi", titolo: "Preventivi" },
  { id: "dopo-la-firma", titolo: "Dopo la firma" },
  { id: "assistenza", titolo: "Assistenza" },
  { id: "messaggi", titolo: "Messaggi" },
  { id: "numeri", titolo: "I numeri" },
  { id: "titolare", titolo: "Solo per il titolare" },
];

const NUOVO = "[data-testid=nuovo-trigger]";
const TOAST_ANNULLA = '[data-sonner-toast] button:has-text("Annulla")';
const DI_CHIARA = (t) => `[data-testid=${t}-{cliente:Chiara De Luca}]`;
// I totali del fornitore stanno nello script (scripts/guide-ricette.mjs, TOTALI), non qui: questo file è nel bundle
// pubblico, e con i totali e un prezzo delle foto si risalirebbe ai ricarichi.
const DUE_TOTALI = [
  { riempi: "[data-testid=calc-input-listino]", con: "{totale:listino}" },
  { riempi: "[data-testid=calc-input-promo]", con: "{totale:promo}" },
];
const RATA_CHIARA = '[data-testid^=rata-]:has-text("Chiara De Luca")';
const I_DUE_TOTALI = "[data-testid=calc-input-listino] >> xpath=ancestor::*[.//*[@data-testid='calc-input-promo']][1]";

export const GUIDE = [
  // ── Primi passi ───────────────────────────────────────────────────────────
  {
    id: "primo-accesso",
    gruppo: "primi-passi",
    titolo: "Entrare la prima volta",
    quando: "Ti serve quando ti arriva l'invito al gestionale e c'è da scegliere la tua password.",
    pubblica: true,
    foto: { come: "nessuno", da: "/auth/benvenuto?token_hash={invito}&type=invite", prepara: "invito" },
    passi: [
      { testo: "Apri l'email di «Interni D'Autore · Gestionale» e premi «Scegli la password». Il link vale 24 ore e si usa una volta sola. Se l'email non c'è, guarda nella posta indesiderata." },
      {
        testo: "Scegli una password di almeno 10 caratteri e scrivila due volte, uguale.",
        prima: [
          { riempi: "[data-testid=benvenuto-password]", con: "{password}" },
          { riempi: "[data-testid=benvenuto-ripeti]", con: "{password}" },
        ],
        punta: "form:has([data-testid=benvenuto-password])",
        premi: false,
      },
      { testo: "Premi «Salva ed entra». Resta spento finché la password non ha 10 caratteri.", punta: "[data-testid=benvenuto-salva]", poi: "[data-testid=totp-qr-code]" },
      { testo: "Se non sei il titolare, sei già dentro: la guida per te finisce qui. Se sei il titolare, ora attivi il codice di verifica, che per te è obbligatorio. Scarica sul telefono Google Authenticator (è gratis), aprilo, premi «+» e poi «Scansiona un codice QR»." },
      {
        testo: "Inquadra il QR col telefono. In Authenticator compare una voce nuova, «Interni D'Autore» con la tua email, e sei cifre che cambiano ogni 30 secondi. Se stai facendo tutto dal telefono e non puoi inquadrarlo, in Authenticator scegli «Inserisci una chiave di configurazione» e copia la chiave scritta sotto il QR.",
        punta: "[data-testid=totp-qr-code]",
        premi: false,
      },
      {
        testo: "Scrivi le sei cifre e premi «Attiva e accedi al gestionale».",
        prima: [{ codiceDa: "[data-testid=totp-secret-key]", in: "[data-testid=totp-code-input] input" }],
        punta: "[data-testid=totp-verify-btn]",
        poi: "[data-testid=page-cruscotto]", // «Sei dentro»: l'invitato di prova è un titolare
      },
    ],
    dopo: "Sei dentro. Il link dell'email non serve più: le volte dopo entri da gestionale.internidautore.com con l'email e la password e, se hai il codice di verifica, le sei cifre di Authenticator.",
    seNonVa: "Se il link dice che è già usato e la password l'hai già scelta, entra da gestionale.internidautore.com. Se è scaduto prima che tu scegliessi la password, chiedi a chi ti ha invitato di rimandartelo. Se le cifre non vengono accettate, controlla di leggere la voce giusta: ogni volta che riprovi, il gestionale mostra un QR nuovo. Cancella da Authenticator la voce di prima e inquadra di nuovo.",
  },
  {
    id: "entrare",
    gruppo: "primi-passi",
    titolo: "Entrare nel gestionale",
    quando: "Ti serve ogni volta che il gestionale ti chiede di entrare.",
    pubblica: true,
    foto: { come: "nessuno", da: "/login" },
    passi: [
      {
        testo: "Apri gestionale.internidautore.com (o l'icona sulla schermata Home) e scrivi la tua email e la password.",
        prima: [
          { riempi: "[data-testid=login-email-input]", con: "{email:titolare}" },
          { riempi: "[data-testid=login-password-input]", con: "{password}" },
        ],
        punta: "[data-testid=login-email-input] >> xpath=ancestor::*[.//*[@data-testid='login-password-input']][1]",
        premi: false,
      },
      { testo: "Premi «Accedi al gestionale».", punta: "[data-testid=login-submit-btn]", poi: "[data-testid=totp-challenge-input]" },
      {
        testo: "Se hai il codice di verifica (il titolare ce l'ha sempre), apri Authenticator, scrivi le sei cifre della voce «Interni D'Autore» e premi «Verifica e continua». Se la voce non c'è, cerca «gestionale.internidautore.com»: si chiama così se hai attivato il codice tempo fa.",
        prima: [{ riempi: "[data-testid=totp-challenge-input] input", con: "{codice:titolare}" }],
        punta: "[data-testid=totp-challenge-submit-btn]",
        poi: "[data-testid=page-cruscotto]",
      },
    ],
    dopo: "Entri nella tua pagina: il titolare nella Dashboard, chi vende in «Oggi».",
    seNonVa: "Se le cifre non vanno, aspetta che cambino e scrivi quelle nuove: durano 30 secondi. Se hai cambiato telefono e in Authenticator la voce del gestionale non c'è più, scrivi a chi segue il gestionale per voi: azzera il codice, e al prossimo ingresso ne attivi uno nuovo.",
  },
  {
    id: "codice-sicurezza",
    gruppo: "primi-passi",
    titolo: "Attivare il codice di verifica, o spostarlo su un altro telefono",
    quando: "Ti serve per proteggere il tuo accesso con le sei cifre di Authenticator, o quando cambi telefono.",
    vai: { label: "Apri «Il tuo accesso»", to: "/sicurezza" },
    foto: { come: "venditore", da: "/oggi" },
    passi: [
      { testo: "Premi il tuo nome: sul computer è in fondo al menu blu, sul telefono è il cerchio con l'iniziale in alto a destra.", punta: "[data-testid=user-menu-trigger]" },
      { testo: "Premi «Il tuo accesso».", punta: "[data-testid=user-menu-sicurezza]", poi: "[data-testid=page-sicurezza]" },
      { testo: "Sotto il tuo profilo c'è «Codice di verifica». Premi «Attiva il codice di verifica».", punta: "[data-testid=btn-attiva-totp]", poi: "[data-testid=totp-qr-code]" },
      { testo: "Apri Google Authenticator sul telefono, premi «+» e inquadra il QR. Se stai facendo tutto dal telefono e non puoi inquadrarlo, in Authenticator scegli «Inserisci una chiave di configurazione» e copia la chiave scritta sotto il QR.", punta: "[data-testid=totp-qr-code]", premi: false },
      {
        testo: "Scrivi le sei cifre che compaiono in Authenticator e premi «Attiva».",
        prima: [{ codiceDa: "[data-testid=totp-secret-key]", in: "[data-testid=totp-code-input] input" }],
        punta: "[data-testid=btn-conferma-totp]",
        poi: "text=Codice di verifica attivo",
      },
      { testo: "Per spostarlo su un telefono nuovo: torna qui, premi «Spostalo su un altro telefono» e poi «Sposta il codice». Il gestionale ti fa collegare subito il telefono nuovo, con gli stessi passi di sopra. Se non sei il titolare il pulsante si chiama «Togli il codice di verifica»: lo togli e lo attivi di nuovo quando vuoi." },
    ],
    dopo: "Da adesso, quando entri, dopo la password il gestionale ti chiede anche le sei cifre.",
    seNonVa: "Se le cifre non vanno, scrivi quelle che Authenticator mostra adesso: cambiano ogni 30 secondi. Se hai già cambiato telefono e non riesci più a entrare, scrivi a chi segue il gestionale per voi: azzera il codice.",
  },
  {
    id: "collegare-claude",
    gruppo: "primi-passi",
    titolo: "Collegare Claude al gestionale",
    quando: "Ti serve se vuoi chiedere a Claude l'agenda, lo stato di un cliente o una bozza di preventivo, senza copiare niente a mano.",
    voce: "impostazioni",
    vai: { label: "Apri le impostazioni", to: "/impostazioni" },
    passi: [
      { testo: "In Claude vai in «Personalizza» e poi «Connettori». Premi «+» e scegli «Aggiungi connettore personalizzato». Se i nomi sono un po' diversi sul tuo schermo, cerca la voce «Connettori»." },
      { testo: "Come nome scrivi «Gestionale Interni D'Autore». Come indirizzo scrivi esattamente https://proposte.internidautore.com/mcp, senza barra in fondo. Le altre voci lasciale come sono e premi «Aggiungi»." },
      { testo: "Premi «Connetti». Si apre il gestionale: entra come fai sempre, con la password e il codice di verifica." },
      { testo: "Leggi cosa Claude potrà fare e cosa non potrà fare mai, poi premi «Consenti». Claude si riapre da solo." },
      { testo: "Subito dopo, fai a Claude una domanda qualsiasi sul gestionale, per esempio «Chi ho in agenda domani?». È la prima domanda che completa il collegamento. Se aspetti più di 10 minuti, scollega e ricollega." },
      { testo: "Nel tuo progetto di Claude accendi il connettore «Gestionale Interni D'Autore», così lo usi anche lì con i tuoi listini." },
      { testo: "In Claude vai in «Impostazioni», poi «Privacy», e spegni «Aiuta a migliorare Claude». Così le tue conversazioni non servono ad allenare i modelli." },
      { testo: "Le bozze di preventivo che prepara Claude le trovi nel gestionale, in «Preventivi». Rileggile e mandale da lì: Claude non invia, non firma e non cancella niente." },
    ],
    dopo: "Claude lavora con i tuoi permessi e vede solo il necessario: mai telefoni, email, indirizzi, costi o margini. Per staccarlo vai in Impostazioni, nel gruppo «Collegamenti» apri «Claude collegato» e premi «Scollega»: smette di funzionare subito.",
    seNonVa: "Se Claude dice che non riesce a collegarsi, controlla l'indirizzo: dev'essere identico, senza spazi e senza barra in fondo. Se il gestionale dice che la richiesta non c'è più, riparti da Claude. Se Claude smette di rispondere dopo che hai rifatto l'accesso al gestionale col codice di verifica, è normale: quell'accesso chiude gli altri collegamenti. In Impostazioni, nel gruppo «Collegamenti», apri «Claude collegato» e premi «Scollega», poi collegalo di nuovo dal primo passo.",
  },
  {
    id: "home-telefono",
    gruppo: "primi-passi",
    titolo: "Mettere il gestionale sulla schermata Home del telefono",
    quando: "Ti serve per aprire il gestionale con un tocco, come un'app, senza cercarlo nel browser.",
    passi: [
      { testo: "Su iPhone: apri il gestionale con Safari, premi il quadrato con la freccia in su (Condividi) e poi «Aggiungi alla schermata Home»." },
      { testo: "Su Android: apri il gestionale con Chrome, premi i tre puntini in alto a destra e poi «Aggiungi a schermata Home» (a volte si chiama «Installa app»)." },
      { testo: "Premi «Aggiungi». Sulla schermata Home compare l'icona di Interni D'Autore." },
    ],
    dopo: "D'ora in poi apri il gestionale dall'icona: si apre a schermo intero, come un'app.",
    seNonVa: "Su iPhone la voce c'è solo in Safari: se usi Chrome o un'altra app, apri l'indirizzo in Safari e riprova.",
  },
  {
    id: "menu-nuovo",
    gruppo: "primi-passi",
    titolo: "Il menu e il pulsante «Nuovo»",
    quando: "Ti serve per orientarti la prima volta che entri.",
    ruoli: ["titolare", "venditore", "amministrazione"],
    foto: { come: "venditore", da: "/oggi" },
    passi: [
      {
        testo: "Le sezioni stanno nel menu: sul computer è la colonna blu a sinistra; sul telefono le tre più usate sono in basso e le altre dentro «Menu».",
        punta: { computer: 'aside nav[aria-label="Menu principale"]', telefono: "[data-testid=bottom-nav-more] >> xpath=.." },
        premi: false,
      },
      { testo: "«Nuovo» ti porta dritto alle cose di tutti i giorni. Premilo.", punta: NUOVO },
      {
        testo: "Scegli cosa aggiungere fra le voci del tuo lavoro, per esempio un cliente o un appuntamento. Si apre la pagina giusta, già pronta.",
        punta: { computer: "[role=menu]", telefono: "[data-testid=nuovo-foglio]" },
        premi: false,
      },
    ],
    dopo: "Per ogni cosa che si fa qui c'è una guida, in «Guide».",
    seNonVa: "Se nel menu non trovi una sezione, il tuo ruolo non la usa: chiedi al titolare di accenderla.",
  },
  {
    id: "oggi",
    gruppo: "primi-passi",
    titolo: "La pagina «Oggi»",
    quando: "Ti serve per sapere cosa fare appena entri, se vendi.",
    voce: "oggi",
    vai: { label: "Apri «Oggi»", to: "/oggi" },
    foto: { come: "venditore", da: "/oggi", prepara: "giornata" },
    passi: [
      { testo: "«Oggi» è la tua pagina: si apre appena entri. Sotto il saluto c'è quante cose hai da fare.", punta: "[data-testid=oggi-sottotitolo]", premi: false },
      { testo: "In «In agenda oggi» trovi le visite di oggi. Quando il cliente esce, segni lì com'è andata, con un tocco.", punta: "[data-testid=oggi-visite]", premi: false },
      { testo: "In «Passate a te su WhatsApp» ci sono i clienti che su WhatsApp aspettano una risposta da una persona. Premi «Apri la conversazione» per rispondere.", punta: "[data-testid=oggi-messaggi]", premi: false },
      { testo: "Più sotto trovi i preventivi senza risposta da più di una settimana, da richiamare, e i tuoi clienti in corsa, fase per fase.", punta: "[data-testid=oggi-clienti]", premi: false },
    ],
    dopo: "Quando segni com'è andata una visita, sparisce da «Da fare oggi».",
    seNonVa: "Se una parte non c'è, la sezione che la riempie è spenta per te: per esempio senza Calendario non vedi le visite di oggi.",
  },

  // ── Clienti ───────────────────────────────────────────────────────────────
  {
    id: "nuovo-cliente",
    gruppo: "clienti",
    titolo: "Aggiungere un nuovo cliente",
    quando: "Ti serve quando qualcuno ti scrive su WhatsApp o su Instagram, ti telefona o passa in showroom, e vuoi segnarlo.",
    voce: "clienti",
    ruoli: ["titolare", "venditore"],
    vai: { label: "Nuovo cliente", to: "/clienti?nuovo=1" },
    foto: { come: "venditore", da: "/clienti" },
    passi: [
      { testo: "Premi «Nuovo». Sul computer è in alto nel menu blu, sul telefono in basso a destra.", punta: NUOVO },
      { testo: "Premi «Cliente», anche per chi è appena entrato in showroom.", punta: "[data-testid=nuovo-voce-cliente]" },
      {
        testo: "Scrivi nome e cognome e il numero di telefono. Il numero serve: se ti ha scritto su Instagram senza lasciarlo, chiediglielo prima. Mentre lo scrivi, il gestionale controlla se il cliente c'è già.",
        prima: [
          { riempi: "[data-testid=nuovo-cliente-nome]", con: "Sara Colombo" },
          { riempi: "[data-testid=nuovo-cliente-telefono]", con: "333 090 4182" },
          { attendi: "[data-testid=nuovo-cliente-verifica] svg" },
        ],
        punta: "[data-testid=nuovo-cliente-nome] >> xpath=../..",
        premi: false,
      },
      {
        testo: "Scegli come ci ha conosciuti e da dove ti ha scritto. Se ha visto la pubblicità su Facebook o Instagram, premi «Campagna Meta», e anche «WhatsApp» se poi ti ha scritto lì: puoi sceglierne più di una.",
        prima: [{ premi: "[data-testid=fonte-whatsapp]" }],
        punta: "[data-testid=nuovo-cliente-fonti]",
        premi: false,
      },
      {
        testo: "Scegli in che fase entra. Se deve ancora venire in showroom, lascia «Nuovo appuntamento». Se è appena entrato senza prenotare, scegli «Appuntamento fatto».",
        punta: "[data-testid=nuovo-cliente-fasi]",
        premi: false,
      },
      {
        testo: "Se sai già quando viene, scegli il giorno e poi uno degli orari liberi. Se non lo sai ancora, lascia vuoto: la visita la fissi dopo.",
        prima: [
          { giorno: "[data-testid=nuovo-cliente-giorno]" },
          { premi: "[data-testid=nuovo-cliente-ora-orari] [role=group] button" },
        ],
        punta: "[data-testid=nuovo-cliente-visita]",
        premi: false,
      },
      {
        testo: "Premi «Salva e fissa la visita»: al cliente parte la conferma su WhatsApp con giorno, ora e indirizzo (di notte aspetta il mattino). Se non hai scelto il giorno, il pulsante dice solo «Salva» e non parte niente.",
        punta: "[data-testid=nuovo-cliente-salva]",
        poi: "text=Cliente aggiunto",
      },
    ],
    dopo: "Il cliente compare in «Clienti», nella fase che hai scelto, e lo segui tu.",
    seNonVa: "Se «Salva» resta grigio, manca il nome o il telefono, oppure hai scelto il giorno senza l'orario. Se il numero è già in archivio, premi «Apri la sua scheda» invece di creare un doppione.",
  },
  {
    id: "trovare-cliente",
    gruppo: "clienti",
    titolo: "Ritrovare un cliente",
    quando: "Ti serve quando ti chiama un cliente e vuoi aprire subito la sua scheda.",
    voce: "clienti",
    vai: { label: "Apri i clienti", to: "/clienti" },
    foto: { come: "venditore", da: "/oggi" },
    passi: [
      { testo: "Apri «Clienti».", punta: { computer: "[data-testid=nav-clienti]", telefono: "[data-testid=bottom-nav-clienti]" }, poi: "[data-testid=clienti-cerca]" },
      {
        testo: "Scrivi in «Cerca nome o telefono» il nome, il cognome o qualche cifra del numero.",
        prima: [{ riempi: "[data-testid=clienti-cerca]", con: "Chiara" }],
        punta: "[data-testid=clienti-cerca]",
        premi: false,
      },
      { testo: "Premi il nome del cliente.", punta: DI_CHIARA("cliente-link"), poi: "[data-testid=cliente-adesso]" },
      { testo: "Nella scheda trovi tutto: i contatti, le visite, i preventivi e, in alto, cosa c'è da fare adesso.", punta: "[data-testid=cliente-adesso]", premi: false },
    ],
    dopo: "Dalla scheda fissi una visita, prepari un preventivo o segni il cliente perso.",
    seNonVa: "Se non lo trovi, prova col numero di telefono o col solo cognome. Se lo segue un collega, fra i tuoi clienti non compare: chiedi al titolare.",
  },
  {
    id: "spostare-cliente",
    gruppo: "clienti",
    titolo: "Spostare un cliente da una colonna all'altra",
    quando: "Ti serve per tenere il Kanban in ordine, per esempio quando un cliente sta trattando sul preventivo.",
    voce: "clienti",
    ruoli: ["titolare", "venditore"],
    vai: { label: "Apri il Kanban", to: "/clienti?vista=kanban" },
    foto: { come: "venditore", da: "/clienti?vista=kanban", prepara: "preventivo-inviato" },
    passi: [
      { testo: "Apri «Clienti» e scegli «Kanban»: una colonna per ogni punto del percorso.", punta: "[data-testid=vista-kanban]" },
      {
        testo: "Quasi sempre il cliente si sposta da solo: quando segni com'è andata la visita, quando il preventivo parte, quando il contratto è firmato.",
        punta: "[data-testid=kanban-riepilogo]",
        premi: false,
      },
      {
        testo: "A mano sposti chi sta trattando sul preventivo: portalo in «In trattativa». Sul computer trascina la scheda nella colonna; sul telefono premi le due frecce sulla scheda.",
        punta: { computer: DI_CHIARA("kanban-carta"), telefono: DI_CHIARA("kanban-sposta") },
        gesto: { computer: [{ tasti: ["Space", "ArrowRight", "Space"], su: DI_CHIARA("kanban-carta") }] },
      },
      {
        testo: "Sul telefono scegli «In trattativa». Sul computer la scheda è già nella colonna nuova.",
        punta: { telefono: 'role=menuitem[name="In trattativa"]', computer: "[data-testid=kanban-colonna-in_trattativa]" },
        gesto: { computer: [] },
        poi: TOAST_ANNULLA,
      },
      { testo: "Ti sei sbagliato? Premi «Annulla» nel messaggio che compare, entro qualche secondo.", punta: TOAST_ANNULLA, premi: false },
    ],
    dopo: "Il cliente resta nella colonna nuova finché succede qualcos'altro, per esempio la firma del contratto.",
    seNonVa: "Se compare una finestra che spiega perché non si può, segui il pulsante che ti propone, per esempio «Fissa la visita»: quella colonna dice un fatto, e il fatto si segna da lì.",
  },
  {
    id: "mettere-in-pari",
    gruppo: "clienti",
    titolo: "Mettere in pari un cliente seguito fuori",
    quando: "Ti serve per un cliente che hai seguito fuori dal gestionale: ha già firmato o pagato, ma il suo percorso è fermo all'ingresso.",
    voce: "clienti",
    ruoli: ["titolare", "venditore"],
    vai: { label: "Apri i clienti", to: "/clienti" },
    foto: { come: "venditore", da: "/clienti/{cliente:Chiara De Luca}" },
    passi: [
      { testo: "Apri la scheda del cliente e, nel riquadro «Il percorso», premi «Aggiorna percorso».", punta: "[data-testid=percorso-aggiorna]", poi: "[data-testid=percorso-modal]" },
      {
        testo: "In «Fase» scegli l'ultima cosa che è già successa, per esempio «Consegna».",
        prima: [{ scegli: "[data-testid=percorso-riga-0-tappa]", valore: "consegna" }],
        punta: "[data-testid=percorso-riga-0-tappa]",
        premi: false,
      },
      {
        testo: "La finestra elenca le fasi rimaste aperte prima di quella e chiede cosa è successo. Premi «Aggiungi queste fasi» per segnarle tutte.",
        punta: "[data-testid=percorso-aggiungi-aperte-0]",
        poi: "[data-testid=percorso-riga-6]",
      },
      {
        testo: "Per ogni fase scegli «Fatto» o «Non c'è stato», per esempio quando non c'è stato un rilievo. Se sai il giorno scrivilo, se no lascia vuoto.",
        prima: [{ premi: "[data-testid=percorso-riga-2-saltato]" }],
        punta: "[data-testid=percorso-riga-2-saltato]",
        premi: false,
      },
      { testo: "Premi «Salva».", punta: "[data-testid=percorso-salva]", poi: "text=Percorso aggiornato" },
      { testo: "Se hai in mano il preventivo o il contratto firmato, usa «Fatto fuori»: i soldi entrano nei numeri solo da lì." },
    ],
    dopo: "Le fasi segnate hanno il segno «A mano». Il cliente passa da solo nella colonna giusta del Kanban (con un contratto segnato, in «Chiuso») e «Adesso» non ti propone più ciò che hai già fatto. Un contratto o un acconto segnati così non entrano nei numeri dei soldi. Se hai sbagliato, riapri «Aggiorna percorso» e premi «Togli» accanto alla fase.",
    seNonVa: "«Salva» resta grigio finché una fase scelta ha davanti fasi aperte senza risposta: rispondi su ognuna, o premi «Aggiungi queste fasi». Il primo acconto e il saldo li segna l'amministrazione, e la finestra te lo dice. Se il cliente ha un contratto firmato nel gestionale, ordine, conferma e merce si segnano dal contratto.",
  },
  {
    id: "cliente-perso",
    gruppo: "clienti",
    titolo: "Segnare un cliente come perso",
    quando: "Ti serve quando un cliente sceglie un altro negozio, rimanda o non risponde più.",
    voce: "clienti",
    ruoli: ["titolare", "venditore"],
    vai: { label: "Apri i clienti", to: "/clienti" },
    foto: { come: "venditore", da: "/clienti/{cliente:Andrea Moretti}" },
    passi: [
      { testo: "Apri la scheda del cliente e premi «Segna perso».", punta: "[data-testid=btn-segna-perso]", poi: "[data-testid=perso-modal]" },
      { testo: "Scegli il motivo con un tocco.", prima: [{ premi: "[data-testid=perso-scelta-1]" }], punta: "[data-testid=perso-scelta-1]", premi: false },
      {
        testo: "Se vuoi, scrivi una nota. Se pensi che tornerà, spunta «Da riprendere fra 3 mesi»: te lo ricorda il gestionale.",
        prima: [
          { riempi: "[data-testid=perso-motivo]", con: "Ha preso la cucina da un mobilificio di Pescara" },
          { premi: "[data-testid=perso-riprendi]" },
        ],
        punta: "[data-testid=perso-riprendi]",
        premi: false,
      },
      { testo: "Premi «Segna perso».", punta: "[data-testid=perso-salva]", poi: "text=Segnato perso" },
    ],
    dopo: "Il cliente va nella colonna «Perso». Se torna, apri la scheda e premi «Riapri».",
    seNonVa: "Il pulsante resta grigio finché non scegli un motivo o scrivi una nota.",
  },
  {
    id: "eliminare-cliente",
    gruppo: "clienti",
    titolo: "Eliminare un cliente",
    quando: "Ti serve per togliere una scheda inserita per sbaglio o doppia.",
    voce: "clienti",
    ruoli: ["titolare"],
    vai: { label: "Apri i clienti", to: "/clienti" },
    foto: { come: "titolare", da: "/clienti/{cliente:Andrea Moretti}" },
    passi: [
      { testo: "Apri la scheda del cliente e premi «Elimina». Sul telefono è il cestino.", punta: "[data-testid=btn-elimina]", poi: "[data-testid=elimina-modal]" },
      { testo: "Leggi cosa sparisce e premi «Elimina per sempre». Non si torna indietro.", punta: "[data-testid=elimina-conferma]", poi: "text=Cliente eliminato" },
    ],
    dopo: "La scheda sparisce con le visite passate, le assistenze e i documenti. Le conversazioni WhatsApp restano, non più collegate a una scheda.",
    seNonVa: "Se il cliente ha già un preventivo o un contratto, non si elimina: usa «Segna perso». Se ha una visita in programma, annullala prima.",
  },

  // ── Visite e calendario ───────────────────────────────────────────────────
  {
    id: "fissare-visita",
    gruppo: "visite",
    titolo: "Fissare una visita in showroom",
    quando: "Ti serve quando un cliente vuole venire a vedere, o per un rilievo misure o una consegna.",
    voce: "calendario",
    ruoli: ["titolare", "venditore"],
    vai: { label: "Nuovo appuntamento", to: "/calendario?nuovo=1" },
    foto: { come: "venditore", da: "/oggi" },
    passi: [
      { testo: "Premi «Nuovo».", punta: NUOVO },
      { testo: "Premi «Appuntamento».", punta: "[data-testid=nuovo-voce-appuntamento]", poi: "[data-testid=nuovo-appuntamento-modal]" },
      {
        testo: "Premi «Scegli il cliente», scrivi il nome e premi il cliente giusto.",
        prima: [{ premi: "[data-testid=nuovo-app-cliente]" }, { riempi: "[data-testid=nuovo-app-cliente-cerca]", con: "Davide" }],
        punta: '[role=option]:has-text("Davide Ricci")',
      },
      {
        testo: "Lascia «Showroom» se viene in negozio. Per un rilievo o una consegna scegli il tipo giusto.",
        punta: "[data-testid=nuovo-app-tipo-visita_showroom] >> xpath=..",
        premi: false,
      },
      { testo: "Scegli il giorno.", punta: 'role=button[name="{giorno-nome}"]' },
      {
        testo: "Scegli uno degli orari liberi: sono quelli che il calendario online non ha ancora dato a nessuno.",
        punta: "[data-testid=nuovo-app-ora-orari]",
        gesto: [{ premi: "[data-testid=nuovo-app-ora-orari] [role=group] button" }],
      },
      { testo: "Premi «Fissa e manda la conferma».", punta: "[data-testid=nuovo-app-salva]", poi: "text=Fissata" },
    ],
    dopo: "Al cliente arriva subito su WhatsApp la conferma con giorno, ora e indirizzo, e prima della visita un promemoria. L'orario si occupa anche sul calendario online: dal sito nessuno può più prenotarlo. Dalla scheda del cliente «Fissa visita» apre questa stessa finestra lì, col cliente già scelto: fissi e resti sulla scheda.",
    seNonVa: "Se dice che l'orario è occupato, qualcuno l'ha appena preso dal sito: scegline un altro. Se manca il telefono del cliente, aggiungilo nella sua scheda e riprova.",
  },
  {
    id: "spostare-visita",
    gruppo: "visite",
    titolo: "Spostare o annullare una visita",
    quando: "Ti serve quando il cliente ti chiede un altro giorno, o non può più venire.",
    voce: "calendario",
    ruoli: ["titolare", "venditore"],
    vai: { label: "Apri il calendario", to: "/calendario" },
    foto: { come: "venditore", da: "/calendario?giorno={giorno-visita:Davide Ricci}", prepara: "visita-futura" },
    passi: [
      { parte: "Spostare", testo: "Apri il Calendario e premi la visita.", punta: "[data-testid=appuntamento-{visita:Davide Ricci}]", poi: "[data-testid=dettaglio-appuntamento]" },
      { testo: "Premi «Sposta».", punta: "[data-testid=dettaglio-sposta]", poi: "[data-testid=sposta]" },
      {
        testo: "Scegli il giorno nuovo e uno degli orari liberi.",
        prima: [{ premi: "[data-testid=sposta] [role=group] button:last-child" }],
        punta: "[data-testid=sposta]",
        premi: false,
      },
      { testo: "Premi «Sposta».", punta: "[data-testid=sposta-salva]", poi: "text=Spostata" },
      { parte: "Annullare", testo: "Per annullarla invece: apri la visita e, sotto «Com'è andata?», premi «Annullata».", punta: "[data-testid=esito-annullato]", poi: "[data-testid=dialogo-conferma]" },
      { testo: "Premi «Segna annullato».", punta: "[data-testid=conferma-procedi]", poi: "text=Annullata" },
    ],
    dopo: "Quando la sposti, i promemoria seguono l'orario nuovo, ma il cliente non riceve un messaggio che glielo dice: avvisalo tu. Quando la annulli, l'orario torna libero sul calendario online e i promemoria non partono più; anche qui, diglielo tu.",
    seNonVa: "Nella finestra di conferma, «Annulla» chiude la finestra e lascia la visita com'è: per annullare la visita il pulsante è «Segna annullato».",
  },
  {
    id: "esito-visita",
    gruppo: "visite",
    titolo: "Segnare com'è andata una visita",
    quando: "Ti serve appena il cliente esce dallo showroom, o se non si è presentato.",
    voce: "clienti",
    ruoli: ["titolare", "venditore"],
    vai: { label: "Visite senza esito", to: "/clienti?lista=da_confermare" },
    foto: { come: "venditore", da: "/oggi", prepara: "visita-passata" },
    passi: [
      { testo: "Premi «Nuovo».", punta: NUOVO },
      { testo: "Premi «Com'è andata una visita».", punta: "[data-testid=nuovo-voce-esito]", poi: "[data-testid=vista-kanban]" },
      { testo: "Premi «Kanban»: la visita da segnare è sulla scheda del cliente.", punta: "[data-testid=vista-kanban]", poi: DI_CHIARA("kanban-esito") },
      {
        testo: "Premi «Presentata» se il cliente è venuto, «No show» se non si è presentato.",
        punta: DI_CHIARA("kanban-esito"),
        gesto: [{ premi: DI_CHIARA("kanban-presentata") }],
        poi: TOAST_ANNULLA,
      },
      { testo: "Il cliente passa da solo nella colonna giusta. Se hai sbagliato, premi «Annulla» nel messaggio.", punta: TOAST_ANNULLA, premi: false },
    ],
    dopo: "Con «Presentata» il cliente va in «Appuntamento fatto», e il passo dopo è il preventivo. Con «No show» va in «No show»: se lo segni il giorno stesso e il cliente ha dato il consenso ai messaggi, gli arriva su WhatsApp l'invito a riprenotare.",
    seNonVa: "Se la visita la segue un collega, l'esito lo segna lui. Puoi segnarlo anche dal Calendario: apri la visita e usa «Com'è andata?».",
  },

  // ── Preventivi ────────────────────────────────────────────────────────────
  {
    id: "calcolatore",
    gruppo: "preventivi",
    titolo: "Usare il calcolatore",
    quando: "Ti serve per trasformare il preventivo del fornitore nel prezzo per il cliente, e per trattare senza sbagliare.",
    voce: "calcolatore",
    vai: { label: "Apri il calcolatore", to: "/preventivi/calcolatore" },
    foto: { come: "venditore", da: "/oggi" },
    passi: [
      { parte: "Il prezzo", testo: "Premi «Nuovo». Il calcolatore lo trovi anche in «Preventivi», nella scheda «Calcolatore».", punta: NUOVO },
      { testo: "Premi «Preventivo»: si apre il calcolatore.", punta: "[data-testid=nuovo-voce-preventivo]", poi: "[data-testid=calc-input-listino]" },
      {
        testo: "Scrivi il «Totale listino» e il «Totale promotion» del preventivo del fornitore, senza IVA, con la virgola per i centesimi. «Carica il PDF del fornitore» prova a trovarli da solo, ma è ancora in prova: controlla sempre le cifre sul foglio. Nelle foto i numeri sono sfocati: sul tuo schermo li vedi.",
        prima: DUE_TOTALI,
        punta: I_DUE_TOTALI,
        premi: false,
      },
      {
        testo: "Se vuoi mostrare al cliente quanto risparmia, scrivi anche il «Totale composizione, IVA inclusa», che trovi sul PDF del fornitore.",
        prima: [{ riempi: "[data-testid=calc-input-composizione]", con: "34.800,00" }],
        punta: "[data-testid=calc-input-composizione]",
        premi: false,
      },
      {
        testo: "Il prezzo per il cliente compare subito, già al livello più alto. Sotto vedi il listino barrato e quanto risparmia.",
        punta: "[data-testid=calc-headline-price] >> xpath=..",
        premi: false,
      },
      {
        parte: "La trattativa",
        testo: "Se il cliente chiede uno sconto, premi «Scendi»: il prezzo scende di un livello. Sotto il tuo minimo si ferma. «Risali» lo fa risalire.",
        punta: "[data-testid=calc-scendi]",
      },
      {
        testo: "La riga «COD» è un promemoria per te: il primo numero è il prezzo più basso che puoi fare, il secondo il più alto. Al cliente sembra un codice.",
        punta: "[data-testid=calc-cod]",
        premi: false,
      },
      { parte: "Il preventivo", testo: "Quando il prezzo va bene, premi «Crea il preventivo da questo calcolo».", punta: "[data-testid=calc-crea-preventivo]", poi: "[data-testid=calc-crea-modal]" },
      {
        testo: "Scegli il cliente e cosa fornisci: cucina, divano, camera… Titolo e nome della configurazione sono facoltativi. Il cliente deve essere già in archivio: se è appena entrato, aggiungilo prima di fare il calcolo, perché uscendo da qui il calcolo si perde.",
        prima: [{ premi: "[data-testid=calc-crea-cliente]" }, { premi: '[role=option]:has-text("Chiara De Luca")' }],
        punta: "[data-testid=calc-crea-cliente]",
        premi: false,
      },
      { testo: "Premi «Crea e apri il preventivo».", punta: "[data-testid=calc-crea-submit]", poi: "text=Preventivo creato" },
    ],
    dopo: "Si apre il preventivo con il prezzo già dentro. Per completarlo e approvarlo segui «Fare un nuovo preventivo», dalla parte «Completare il preventivo».",
    seNonVa: "Se il prezzo non compare, controlla di aver scritto tutti e due i totali. Se «Scendi» è grigio, sei già al tuo minimo. Se il cliente non è nell'elenco, non è ancora in archivio: aggiungilo e rifai il calcolo.",
  },
  {
    id: "nuovo-preventivo",
    gruppo: "preventivi",
    titolo: "Fare un nuovo preventivo",
    quando: "Ti serve dopo la visita, quando hai il preventivo del fornitore e vuoi mandare la proposta al cliente.",
    voce: "preventivatore",
    vai: { label: "Apri i preventivi", to: "/preventivi/preventivatore" },
    foto: { come: "venditore", da: "/preventivi/calcolatore" },
    passi: [
      {
        parte: "Dal calcolatore",
        testo: "Apri il calcolatore («Nuovo», poi «Preventivo») e scrivi i due totali del fornitore. Nelle foto i numeri sono sfocati: sul tuo schermo li vedi.",
        prima: DUE_TOTALI,
        punta: I_DUE_TOTALI,
        premi: false,
      },
      { testo: "Porta il prezzo dove vuoi con «Scendi» o «Risali», poi premi «Crea il preventivo da questo calcolo».", punta: "[data-testid=calc-crea-preventivo]", poi: "[data-testid=calc-crea-modal]" },
      {
        testo: "Scegli il cliente e premi «Crea e apri il preventivo».",
        prima: [{ premi: "[data-testid=calc-crea-cliente]" }, { premi: '[role=option]:has-text("Chiara De Luca")' }],
        punta: "[data-testid=calc-crea-submit]",
        poi: "[data-testid=page-preventivatore-editor]",
      },
      {
        parte: "Completare il preventivo",
        testo: "Scegli se dare del voi o del tu: «Diamo del voi» per una coppia o una famiglia, «Diamo del tu» per una persona sola.",
        prima: [{ premi: "[data-testid=registro-select]" }],
        punta: "[data-testid=registro-voi]",
      },
      {
        testo: "In «Composizione» scrivi da 6 a 8 righe: cosa c'è nel progetto, una cosa per riga. Si salva da sola.",
        prima: [
          { riempi: "[data-testid=comp-row-0]", con: "Basi e pensili in rovere naturale, ante a gola" },
          { riempi: "[data-testid=comp-row-1]", con: "Piano in quarzo bianco, spessore 2 cm" },
          { riempi: "[data-testid=comp-row-2]", con: "Isola con penisola snack e due sgabelli" },
          { riempi: "[data-testid=comp-row-3]", con: "Colonna forno e microonde a incasso" },
          { riempi: "[data-testid=comp-row-4]", con: "Lavello sottotop in acciaio con miscelatore" },
          { riempi: "[data-testid=comp-row-5]", con: "Illuminazione a led sotto i pensili" },
          { tasti: ["Tab"] },
        ],
        punta: "[data-testid=blocco-composizione]",
        premi: false,
      },
      { testo: "In «Scadenza» scegli fino a quando vale il prezzo: 7, 14 o 21 giorni.", punta: "[data-testid=scadenza-preset-14]" },
      { parte: "Approvare", testo: "Quando è tutto a posto, premi «Approva» in alto.", punta: "[data-testid=btn-approva]", poi: "text=Approvato" },
    ],
    dopo: "Il preventivo è pronto da mandare al cliente: segui «Mandare il preventivo al cliente».",
    seNonVa: "Se una sezione dice «Non ancora salvata», tocca fuori dal campo: si salva da sola. Se «Approva» non c'è, il preventivo è già approvato o già mandato.",
  },
  {
    id: "mandare-preventivo",
    gruppo: "preventivi",
    titolo: "Mandare il preventivo al cliente",
    quando: "Ti serve quando il preventivo è approvato e vuoi che il cliente lo veda sul telefono.",
    voce: "preventivatore",
    vai: { label: "Apri i preventivi", to: "/preventivi" },
    foto: { come: "venditore", da: "/preventivi", prepara: "preventivo-approvato" },
    passi: [
      { testo: "Apri «Preventivi» (sul telefono lo trovi sotto «Menu»). Si apre su «Da richiamare»: un preventivo approvato e non ancora mandato sta in «Bozze». Premi «Bozze».", punta: "[data-testid=tab-bozze]", poi: "[data-testid=preventivo-row-{preventivo:Chiara De Luca}]" },
      { testo: "Premi il preventivo da mandare.", punta: "[data-testid=preventivo-row-{preventivo:Chiara De Luca}]", poi: "[data-testid=btn-pubblica]" },
      { testo: "Premi «Manda il preventivo».", punta: "[data-testid=btn-pubblica]", poi: "[data-testid=dialog-link-pubblico]" },
      {
        testo: "Premi il pulsante con le due paginette accanto al link: copia il link.",
        prima: [{ leggi: "[data-testid=link-pubblico-input]", in: ["link", "Chiara De Luca"] }],
        punta: "[data-testid=btn-copy-link]",
        premi: false,
      },
      { testo: "Incolla il link nella chat WhatsApp del cliente, o in un'email, e mandalo. Il gestionale non lo manda da solo." },
      {
        testo: "Quando il cliente lo apre, nella scheda «Inviati» lo vedi scritto: «Aperto una volta».",
        prima: [{ tasti: ["Escape"] }, { scarica: "{link:Chiara De Luca}" }, { vai: "/preventivi" }, { premi: "[data-testid=tab-inviati]" }],
        punta: "[data-testid=aperture-volte]",
        premi: false,
      },
    ],
    dopo: "Il cliente legge la proposta dal telefono e, se va bene, preme «Confermo la proposta». Cosa succede dopo è nella guida «Quando il cliente conferma». Se riapri il preventivo, in alto vedi quante volte l'ha aperto, quando e da che dispositivo; se l'ha aperto da poco e non ha ancora confermato, ti dice che è un buon momento per sentire il cliente.",
    seNonVa: "Se compare «Il preventivo non è pronto per l'invio», la finestra dice cosa manca, per esempio il tu o il voi, o la scadenza. Sistema quel punto e riprova. Per cambiare un preventivo già mandato serve un preventivo nuovo: «Rifai il link» dà solo un indirizzo nuovo allo stesso documento.",
  },
  {
    id: "richiamare-preventivi",
    gruppo: "preventivi",
    titolo: "Richiamare chi non ha risposto al preventivo",
    quando: "Ti serve ogni giorno, per sapere a chi telefonare fra i clienti a cui hai mandato un preventivo e non hanno risposto.",
    voce: "preventivatore",
    vai: { label: "Apri i preventivi", to: "/preventivi" },
    foto: { come: "venditore", da: "/preventivi", prepara: "preventivo-da-richiamare" },
    passi: [
      {
        testo: "Apri «Preventivi» (sul telefono lo trovi sotto «Menu»). Si apre su «Da richiamare»: i preventivi mandati da più di 7 giorni a cui il cliente non ha risposto. In cima c'è chi non ha mai aperto il link, poi chi aspetta da più tempo.",
        punta: "[data-testid=preventivi-schede]",
        premi: false,
      },
      {
        testo: "Sopra ci sono tre riquadri: «Mai aperto», «Aperto, nessuna risposta» e «Scadono entro 7 giorni». Ognuno dice quanti sono e quanto valgono. Premine uno per vedere solo quei preventivi, premilo ancora per tornare a tutti.",
        punta: "[data-testid=riquadro-mai]",
        premi: false,
      },
      {
        testo: "Ogni riga dice il valore, quando l'hai mandato e se il cliente ha aperto il link. Dal telefono premi «Chiama» e parte la chiamata; dal computer il numero lo trovi nella scheda del cliente.",
        punta: "[data-testid^=richiamo-chiama-]",
        premi: false,
      },
      { testo: "Se vuoi rileggere il preventivo prima di chiamare, premi «Apri»." },
    ],
    dopo: "Il preventivo esce da «Da richiamare» da solo quando il cliente conferma, quando nasce il contratto o quando segni il cliente perso.",
    seNonVa: "Se compare «Nessuno da richiamare», nessun preventivo aspetta una risposta da più di 7 giorni: gli altri li trovi in «Inviati». «Chiama» non c'è se il cliente non ha il telefono nella scheda.",
  },
  {
    id: "cliente-conferma",
    gruppo: "preventivi",
    titolo: "Quando il cliente conferma",
    quando: "Ti serve per sapere cosa vede il cliente e cosa succede quando dice sì alla proposta.",
    voce: "preventivatore",
    vai: { label: "Apri i contratti", to: "/contratti" },
    foto: { come: "venditore", da: "/oggi", prepara: "preventivo-inviato" },
    passi: [
      {
        testo: "Il cliente apre il link e vede la proposta: le parti del progetto, il prezzo e fino a quando vale.",
        prima: [{ vai: "{link:Chiara De Luca}" }],
        punta: "h1",
        premi: false,
      },
      { testo: "In fondo trova «Confermo la proposta».", punta: "a[href*='/confermo']", poi: "text=Grazie, conferma ricevuta" },
      { testo: "Gli si apre «Grazie, conferma ricevuta», con scritto quando è stata registrata e che nei prossimi giorni lo sentirete per fissare il rilievo.", punta: "h1", premi: false },
      { testo: "Nel gestionale nasce il contratto, in bozza: lo trovi in «Contratti».", prima: [{ vai: "/contratti" }], punta: '[data-testid^=contratto-row-]:has-text("Chiara De Luca")', premi: false },
    ],
    dopo: "Il cliente non è ancora «Chiuso»: lo diventa quando firma in negozio. Il contratto lo prepari con la guida «Il contratto: piano di pagamento e firma».",
    seNonVa: "Se il cliente ti scrive che il link non va, può essere scaduto: apri il preventivo e premi «Rifai il link», poi mandagli il link nuovo.",
  },
  {
    id: "fatto-fuori",
    gruppo: "preventivi",
    titolo: "Registrare un preventivo o un contratto fatto fuori",
    quando: "Ti serve quando un preventivo o un contratto è nato su carta o in un altro programma, e vuoi il cliente nella colonna giusta con il suo file.",
    voce: "preventivatore",
    ruoli: ["titolare", "venditore"],
    vai: { label: "Apri i clienti", to: "/clienti" },
    foto: { come: "titolare", da: "/clienti/{cliente:Chiara De Luca}", prepara: "preventivo-inviato" },
    passi: [
      { parte: "Un preventivo", testo: "Apri la scheda del cliente e premi «Fatto fuori».", punta: "[data-testid=btn-fatto-fuori]", poi: "[data-testid=fuori-dialogo]" },
      { testo: "Scegli «Un preventivo». Se sei un venditore questo passo salta: la finestra si apre già sul preventivo.", punta: "[data-testid=fuori-scelta-preventivo]", poi: "[data-testid=fuori-form-preventivo]" },
      {
        testo: "Premi «Scegli un file o scatta una foto» e prendi il preventivo: un PDF, oppure una foto fatta col telefono. Senza il file non si registra.",
        punta: "[data-testid=fuori-scegli-file]",
        premi: false,
      },
      {
        testo: "Scrivi l'importo totale, IVA compresa, e guarda il giorno in cui il cliente l'ha ricevuto. «Che cos'è» e il titolo si cambiano se serve.",
        prima: [{ allega: "[data-testid=fuori-file]" }, { riempi: "[data-testid=fuori-totale]", con: "18.500" }],
        punta: "[data-testid=fuori-totale]",
        premi: false,
      },
      { testo: "Premi «Registra il preventivo».", punta: "[data-testid=fuori-registra-preventivo]", premi: false },
      {
        parte: "Un contratto firmato",
        testo: "Per un contratto firmato apri di nuovo «Fatto fuori» e scegli «Un contratto firmato». Lo registra solo il titolare.",
        prima: [{ tasti: ["Escape"] }, { premi: "[data-testid=btn-fatto-fuori]" }],
        punta: "[data-testid=fuori-scelta-contratto]",
        poi: "[data-testid=fuori-form-foglio]",
      },
      {
        testo: "Allega il foglio firmato e scrivi il giorno della firma e l'importo totale, IVA compresa.",
        prima: [
          { allega: "[data-testid=fuori-file]" },
          { riempi: "[data-testid=fuori-firma]", con: "2026-10-20" },
          { riempi: "[data-testid=fuori-totale]", con: "18.500" },
        ],
        punta: "[data-testid=fuori-totale]",
        premi: false,
      },
      {
        testo: "Se il cliente ha preventivi già inviati, li trovi qui con la spunta: questo contratto li chiude. Togli la spunta a quelli che devono restare aperti. Se compare l'elenco delle vendite di prima, scegli quella che corrisponde a questo contratto, oppure «No, è una vendita nuova»: finché non scegli, «Avanti» resta grigio.",
        punta: "[data-testid=fuori-preventivi]",
        premi: false,
      },
      { testo: "Premi «Avanti: le rate».", punta: "[data-testid=fuori-avanti-rate]", poi: "[data-testid=piano-form]" },
      {
        testo: "Scrivi l'acconto alla firma: il resto lo propone il gestionale, come per ogni contratto. Controlla che in fondo la riga dica che le rate fanno il totale, poi premi «Avanti».",
        prima: [{ riempi: "[data-testid=piano-primo]", con: "3000" }],
        punta: "[data-testid=piano-salva]",
        poi: "[data-testid=fuori-form-conferma]",
      },
      {
        testo: "Se il cliente ha già pagato l'acconto, spunta «L'acconto l'ho già incassato» e scrivi quando e come.",
        prima: [{ premi: "[data-testid=fuori-acconto-si]" }],
        punta: "[data-testid=fuori-acconto]",
        premi: false,
      },
      { testo: "Premi «Registra il contratto».", punta: "[data-testid=fuori-registra-contratto]", premi: false },
      { parte: "Dal Kanban", testo: "Dal Kanban è lo stesso gesto. Se trascini un cliente su «Preventivo inviato» o su «Chiuso» e il gestionale ti dice che il fatto non c'è, premi «Fatto fuori, allegalo»." },
    ],
    dopo: "Il cliente passa da solo nella colonna giusta: «Preventivo inviato» dopo un preventivo, «Chiuso» dopo un contratto. Il file resta nei documenti del cliente, e nella parte «Dalla piattaforma» lo storico lo mostra con il segno «Fatto fuori». Al cliente non arriva nessun messaggio. Se hai sbagliato, premi «Togli» accanto alla riga dello storico: il contratto si toglie finché non ci sono incassi, fatture o ordini.",
    seNonVa: "Il pulsante resta grigio finché mancano il file, l'importo o il giorno: la riga sotto dice cosa. Se il file non parte, controlla che sia un PDF o una foto e che pesi meno di 25 MB. Se «Un contratto firmato» non c'è, il contratto lo registra il titolare.",
  },
  {
    id: "calcolatore-interno",
    gruppo: "preventivi",
    titolo: "Vedere costi e livelli nel calcolatore",
    quando: "Ti serve per controllare come nasce un prezzo: costo, ricarico e livelli.",
    voce: "calcolatore",
    ruoli: ["titolare"],
    vai: { label: "Apri il calcolatore", to: "/preventivi/calcolatore" },
    foto: { come: "titolare", da: "/preventivi/calcolatore" },
    passi: [
      { testo: "Scrivi i due totali del fornitore, come sempre. Nelle foto i numeri sono sfocati: sul tuo schermo li vedi.", prima: DUE_TOTALI, punta: I_DUE_TOTALI, premi: false },
      { testo: "Accendi «Vista interna»: la vedi solo tu.", punta: "[data-testid=calc-vista-interna-switch]", poi: "[data-testid=calc-level-M5]" },
      {
        testo: "In «Livello di trattativa» M5 è il prezzo più alto e M1 il più basso; accanto c'è il ricarico. I livelli sotto il minimo di un ruolo sono grigi.",
        punta: "[data-testid=calc-level-M5] >> xpath=..",
        premi: false,
      },
      {
        testo: "La «Catena di calcolo» mostra come si arriva al prezzo: listino e promotion scontati, il costo con l'IVA, poi il ricarico.",
        punta: "[data-testid=row-listino] >> xpath=ancestor::*[.//*[@data-testid='row-prezzo']][1]",
        premi: false,
      },
      {
        testo: "Più sotto trovi tutti i livelli e «Il tuo minimo». Il minimo di ogni ruolo lo cambi in «Impostazioni», nel gruppo «Preventivi», alla voce «Prezzi».",
        punta: "[data-testid=calc-floor-price]",
        premi: false,
      },
    ],
    dopo: "La vista interna si spegne ogni volta che riapri il calcolatore: davanti al cliente parti sempre dalla vista normale.",
    seNonVa: "Se un livello è grigio, è sotto il minimo del ruolo: il server non lo accetta neanche da un altro schermo.",
  },

  {
    id: "listini",
    gruppo: "preventivi",
    titolo: "Cercare un prezzo nei listini e salvare la lista",
    quando: "Ti serve per trovare il prezzo di un mobile sui listini dei fornitori e portare la lista al cliente.",
    voce: "listini",
    vai: { label: "Apri i listini", to: "/listini" },
    // Nelle foto resta solo la prima schermata dell'app: dalla ricerca in poi ci sono i prezzi dei fornitori, e le foto sono file pubblici.
    foto: { come: "venditore", da: "/listini" },
    passi: [
      { parte: "Cercare", testo: "Premi «Listini» nel menu. Sul telefono lo trovi sotto «Menu»." },
      {
        testo: "Scegli la marca: ogni riquadro è un listino. Sopra ci sono le schede per tipo di mobile: Armadi, Divani, Letti, Sedie, Tavoli e le altre. «Elettrodomestici Cucina» cerca fra gli elettrodomestici.",
        punta: "[data-testid=listini-iframe]",
        premi: false,
      },
      { testo: "Scrivi cosa cerchi, per esempio «sedia polipropilene», oppure usa i filtri. Premi «＋ Preventivo» accanto alla voce che ti serve: finisce nella lista." },
      { parte: "La lista", testo: "Apri la lista dalla barra blu in fondo. Nel campo «Cliente / riferimento» scrivi per chi è, nelle note quello che serve a chi ordina." },
      { testo: "Premi «Salva nella scheda del cliente», scegli il cliente e premi «Salva nella scheda». La lista diventa un PDF fra i documenti di quel cliente, in «Liste dai listini». «Scarica il PDF» lo scarica senza salvarlo: l'amministrazione usa questo." },
      { testo: "Per ordinare, premi «Prepara mail ordine»: ti prepara le mail per i fornitori, con le voci già scritte." },
      { testo: "Se sei il titolare, «Interni» mostra i prezzi d'acquisto. Il codice e gli sconti per marca li scrivi una volta sola: restano su questo computer, nessun altro li vede." },
    ],
    dopo: "La lista resta salvata finché non la svuoti: puoi chiudere i listini e riprenderla più tardi.",
    seNonVa: "Dopo una pausa di mezz'ora l'app si ricarica da sola, oppure compare «Accesso scaduto»: la lista resta, riapri «Listini» dal menu e riparti da lì. Se la voce nel menu non c'è, chiedi al titolare di accenderla in «Impostazioni», gruppo «Persone», alla voce «Collaboratori e permessi».",
  },

  // ── Dopo la firma ─────────────────────────────────────────────────────────
  {
    id: "contratto-firma",
    gruppo: "dopo-la-firma",
    titolo: "Il contratto: piano di pagamento e firma",
    quando: "Ti serve quando il cliente ha confermato la proposta e viene a firmare.",
    voce: "contratti",
    ruoli: ["titolare", "venditore"],
    vai: { label: "Apri i contratti", to: "/contratti" },
    foto: { come: "venditore", da: "/contratti", prepara: "contratto-bozza" },
    passi: [
      { testo: "Apri «Contratti» e premi il contratto del cliente.", punta: "[data-testid=contratto-row-{contratto:Chiara De Luca}]", poi: "[data-testid=piano-form]" },
      { parte: "Il piano di pagamento", testo: "In «Il piano» scegli come paga: di solito «Acconto, 30% e saldo prima della consegna».", punta: "[data-testid=piano-tipo-quasi_tutti]" },
      {
        testo: "Scrivi l'acconto alla firma. Il 30% dopo il rilievo e il saldo si calcolano da soli: controllali.",
        prima: [{ riempi: "[data-testid=piano-primo]", con: "3000" }],
        punta: "[data-testid=piano-primo]",
        premi: false,
      },
      { testo: "Sotto, la riga deve dire che le rate fanno il totale. Poi premi «Salva il piano».", punta: "[data-testid=piano-salva]", poi: "text=Piano di pagamento salvato" },
      { parte: "La firma", testo: "Premi «Pronto da firmare». Con «Stampa» hai il contratto da far firmare.", punta: "[data-testid=azione-da_firmare]", poi: "[data-testid=azione-firmato]" },
      { testo: "Quando il cliente ha firmato, premi «Segna come firmato».", punta: "[data-testid=azione-firmato]", poi: "[data-testid=firma-modal]" },
      { testo: "Controlla la data di firma e premi «Segna come firmato».", punta: "[data-testid=firma-conferma]", poi: "text=Contratto: firmato" },
    ],
    dopo: "Il cliente passa in «Chiuso» e le rate compaiono in «Fatture e incassi». Carica il contratto firmato con «Carica il firmato».",
    seNonVa: "Se «Pronto da firmare» resta grigio, manca il piano di pagamento o la configurazione: lo dice la scritta accanto.",
  },
  {
    id: "segnare-pagamento",
    gruppo: "dopo-la-firma",
    titolo: "Segnare un pagamento",
    quando: "Ti serve quando arriva un bonifico, un acconto in contanti o un pagamento con la carta.",
    voce: "fatture-incassi",
    vai: { label: "Apri Fatture e incassi", to: "/fatture-incassi" },
    foto: { come: "amministrazione", da: "/fatture-incassi", prepara: "contratto-firmato" },
    passi: [
      { testo: "Apri «Fatture e incassi»: in «Chi deve pagare» trovi le rate di chi ha firmato.", punta: RATA_CHIARA, premi: false },
      { testo: "Premi «Segna incasso» sulla rata pagata.", punta: `${RATA_CHIARA} [data-testid^=segna-]`, poi: "[data-testid=incasso-modal]" },
      { testo: "Controlla l'importo e la data, e scegli come ha pagato: bonifico, contanti, carta…", punta: "[data-testid=incasso-importo]", premi: false },
      { testo: "Premi «Segna l'incasso».", punta: "[data-testid=incasso-salva]", poi: "text=segnato" },
    ],
    dopo: "La rata risulta pagata e l'incasso entra nei numeri del mese.",
    seNonVa: "Se la rata non c'è, il contratto non è ancora segnato firmato. Se dice «Su questa rata restano…», l'importo è più alto di quanto resta da pagare. Se compare «Non è arrivata la conferma del salvataggio», quello che hai scritto resta lì: premi «Riprova». Se l'incasso era già arrivato, non viene segnato due volte.",
  },
  {
    id: "ordine-conferma",
    gruppo: "dopo-la-firma",
    titolo: "Segnare l'ordine e la conferma d'ordine",
    quando: "Ti serve quando ordini la merce al fornitore e quando arriva la sua conferma con la settimana di consegna.",
    voce: "clienti",
    ruoli: ["titolare", "venditore", "amministrazione"],
    vai: { label: "Apri i clienti", to: "/clienti" },
    foto: { come: "venditore", da: "/clienti/{cliente:Chiara De Luca}", prepara: "contratto-firmato" },
    passi: [
      { testo: "Apri la scheda del cliente: nella linea del tempo, accanto a «Ordine», premi «Segna ordine».", punta: "[data-testid=tappa-ordine-segna]", poi: "[data-testid=ordine-modal]" },
      { testo: "Controlla il giorno in cui hai ordinato e premi «Salva».", punta: "[data-testid=ordine-salva]", poi: "text=Ordine segnato" },
      { testo: "Quando il fornitore manda la conferma, premi «Segna conferma».", punta: "[data-testid=tappa-conferma-segna]", poi: "[data-testid=conferma-modal]" },
      {
        testo: "Scrivi la settimana di arrivo che c'è sulla conferma: sotto compaiono i giorni di quella settimana.",
        prima: [{ riempi: "[data-testid=conferma-settimana]", con: "{settimana-tra:21}" }],
        punta: "[data-testid=conferma-settimana]",
        premi: false,
      },
      { testo: "Premi «Salva».", punta: "[data-testid=conferma-salva]", poi: "text=Conferma d'ordine segnata" },
    ],
    dopo: "Il gestionale sa quando arriva la merce: da lì si organizzano consegna e montaggio.",
    seNonVa: "La conferma si segna dopo l'ordine, e l'ordine non può avere una data prima della firma.",
  },
  {
    id: "merce-arrivata",
    gruppo: "dopo-la-firma",
    titolo: "Segnare la merce arrivata",
    quando: "Ti serve quando la merce del cliente entra in magazzino.",
    voce: "clienti",
    ruoli: ["titolare", "amministrazione"],
    vai: { label: "Apri i clienti", to: "/clienti" },
    foto: { come: "amministrazione", da: "/clienti/{cliente:Chiara De Luca}", prepara: "merce-confermata" },
    passi: [
      { testo: "Apri la scheda del cliente: accanto a «Merce arrivata», premi «Segna arrivata».", punta: "[data-testid=tappa-merce-segna]", poi: "[data-testid=merce-modal]" },
      { testo: "Controlla il giorno e premi «Salva».", punta: "[data-testid=merce-salva]", poi: "text=Merce arrivata" },
    ],
    dopo: "Nella linea del tempo del cliente la merce risulta arrivata, e si può organizzare la consegna.",
    seNonVa: "Se «Segna arrivata» non c'è, manca prima la conferma d'ordine.",
  },
  {
    id: "consegna-montaggio",
    gruppo: "dopo-la-firma",
    titolo: "Organizzare consegna e montaggio",
    quando: "Ti serve quando sai la settimana di arrivo della merce e vuoi fissare i giorni e la squadra.",
    voce: "calendario",
    ruoli: ["titolare", "amministrazione"],
    vai: { label: "Apri il calendario", to: "/calendario" },
    foto: { come: "amministrazione", da: "/clienti/{cliente:Chiara De Luca}", prepara: "da-montare" },
    passi: [
      { testo: "Apri la scheda del cliente e premi «Organizza consegna e montaggio».", punta: "[data-testid=btn-organizza-montaggio]", poi: "[data-testid=montaggio-modal]" },
      { testo: "Scegli quanti giorni servono: di solito due per una cucina.", punta: "[data-testid=montaggio-quanti-2]" },
      { testo: "Il primo giorno è già il lunedì della settimana di arrivo: cambialo se serve. Premi un giorno per saltarlo; il sabato è già saltato, premilo se quella settimana si lavora.", punta: "[data-testid=montaggio-inizio]", premi: false },
      { testo: "Scegli la squadra.", punta: "[data-testid^=montaggio-squadra-]" },
      { testo: "Premi «Organizza».", punta: "[data-testid=montaggio-salva]", poi: "[data-testid=montaggio-esito]" },
      { testo: "Premi «Avvisa» accanto al montatore: si apre WhatsApp con il messaggio già scritto, da mandare tu.", punta: "[role=dialog] [data-testid^=montaggio-avvisa-]", premi: false },
    ],
    dopo: "I giorni compaiono nel Calendario e quella squadra non si può prenotare due volte negli stessi giorni.",
    seNonVa: "Se compare «Già impegnato», quella squadra quei giorni lavora altrove: cambia i giorni o la squadra.",
  },
  {
    id: "montatori",
    gruppo: "dopo-la-firma",
    titolo: "Aggiungere un montatore all'elenco",
    quando: "Ti serve la prima volta che organizzi un montaggio, o quando arriva un montatore nuovo.",
    voce: "calendario",
    ruoli: ["titolare", "amministrazione"],
    vai: { label: "Apri i clienti", to: "/clienti" },
    foto: { come: "amministrazione", da: "/clienti/{cliente:Chiara De Luca}", prepara: "merce-confermata" },
    passi: [
      { testo: "Apri la scheda di un cliente col contratto firmato e premi «Organizza consegna e montaggio».", punta: "[data-testid=btn-organizza-montaggio]", poi: "[data-testid=montatore-nome]" },
      { testo: "Se l'elenco è vuoto si apre da solo; altrimenti premi «Gestisci l'elenco».", punta: "[data-testid=montatore-nome] >> xpath=ancestor::*[.//*[@data-testid='montatore-aggiungi']][1]", premi: false },
      {
        testo: "Scrivi nome e telefono del montatore e premi «Aggiungi».",
        prima: [
          { riempi: "[data-testid=montatore-nome]", con: "Simone Ferrara" },
          { riempi: "[data-testid=montatore-tel]", con: "333 090 2211" },
        ],
        punta: "[data-testid=montatore-aggiungi]",
        poi: "[data-testid^=montatore-togli-]",
      },
      { testo: "Premi «Torna ai giorni» per organizzare il lavoro.", punta: "[data-testid=montatori-torna]", premi: false },
    ],
    dopo: "Il montatore compare fra le squadre di tutti i lavori. Il telefono serve per avvisarlo su WhatsApp.",
    seNonVa: "Se un montatore non lavora più con voi, premi «Togli» accanto al suo nome.",
  },

  // ── Assistenza ────────────────────────────────────────────────────────────
  {
    id: "nuova-assistenza",
    gruppo: "assistenza",
    titolo: "Aprire un intervento di assistenza",
    quando: "Ti serve quando un cliente già servito segnala un problema: un'anta da regolare, un pezzo da cambiare.",
    voce: "assistenza",
    ruoli: ["titolare", "venditore", "amministrazione"],
    vai: { label: "Nuova assistenza", to: "/assistenza?nuova=1" },
    foto: { come: "venditore", da: "/oggi" },
    passi: [
      { testo: "Premi «Nuovo».", punta: NUOVO },
      { testo: "Premi «Assistenza».", punta: "[data-testid=nuovo-voce-assistenza]", poi: "[data-testid=assistenza-modal]" },
      {
        testo: "Cerca il cliente e sceglilo.",
        prima: [{ premi: "[data-testid=assistenza-cliente]" }, { riempi: "[data-testid=assistenza-cliente-cerca]", con: "Davide" }],
        punta: '[role=option]:has-text("Davide Ricci")',
      },
      {
        testo: "In «Cosa» scrivi in poche parole il problema.",
        prima: [{ riempi: "[data-testid=assistenza-cosa]", con: "Anta del pensile da regolare" }],
        punta: "[data-testid=assistenza-cosa]",
        premi: false,
      },
      { testo: "Se sai già quando andare, scegli il giorno. In «Chi la fa» scegli chi ci va, o lascia «Da assegnare».", punta: "[data-testid=assistenza-chi]", premi: false },
      { testo: "Premi «Salva».", punta: "[data-testid=assistenza-salva]", poi: "text=Assistenza aggiunta" },
    ],
    dopo: "L'intervento compare in «Assistenza», e nel menu cresce il numero di quelli da chiudere. Al cliente non arriva niente in automatico: se vuoi, avvisalo tu.",
    seNonVa: "Se il cliente lo segue un collega, l'assistenza la apre lui, il titolare o l'amministrazione.",
  },
  {
    id: "chiudere-assistenza",
    gruppo: "assistenza",
    titolo: "Chiudere un intervento quando è fatto",
    quando: "Ti serve dopo l'intervento, per toglierlo da quelli da fare.",
    voce: "assistenza",
    vai: { label: "Apri l'assistenza", to: "/assistenza" },
    foto: { come: "venditore", da: "/assistenza", prepara: "assistenza-aperta" },
    passi: [
      { testo: "Apri «Assistenza» e premi l'intervento: i dettagli si aprono accanto all'elenco (sul telefono, al suo posto).", punta: "[data-testid=assistenza-{assistenza:Davide Ricci}]", poi: "[data-testid=assistenza-segna-fatta]" },
      { testo: "Premi «Segna fatta».", punta: "[data-testid=assistenza-segna-fatta]", poi: "text=Assistenza segnata come fatta" },
      { testo: "Se l'intervento si sposta, premi «Sposta» e scegli il giorno nuovo. Se non c'è ancora un giorno, il pulsante dice «Fissa l'intervento». «Modifica» cambia tutto il resto: il problema, chi la fa, lo stato." },
    ],
    dopo: "L'intervento esce da quelli da fare e resta nello storico: lo ritrovi nel gruppo «Fatte» o col filtro «Fatte». Al cliente non arriva niente: se vuoi, avvisalo tu.",
    seNonVa: "Se «Segna fatta» non c'è, l'intervento è di un cliente che segue un collega: lo vedi, ma lo chiude lui.",
  },

  // ── Messaggi ──────────────────────────────────────────────────────────────
  {
    id: "rispondere-whatsapp",
    gruppo: "messaggi",
    titolo: "Rispondere a un cliente su WhatsApp",
    quando: "Ti serve quando un cliente scrive al numero dello showroom e la conversazione è affidata a te.",
    voce: "messaggi",
    vai: { label: "Apri i messaggi", to: "/messaggi" },
    foto: { come: "venditore", da: "/messaggi", prepara: "conversazione" },
    passi: [
      { testo: "Apri «Messaggi» e premi la conversazione. «Da rispondere», in alto, ti mostra quelle in cui l'ultimo messaggio è del cliente.", punta: "[data-testid=conversazione-393330905521]", poi: "[data-testid=filo-testo]" },
      { testo: "Sopra il campo c'è scritto per quanto puoi ancora scrivere: WhatsApp lascia rispondere liberamente entro 24 ore dall'ultimo messaggio del cliente.", punta: "[data-testid=filo-finestra]", premi: false },
      {
        testo: "Scrivi la risposta.",
        prima: [{ riempi: "[data-testid=filo-testo]", con: "Buongiorno Davide, sabato mattina va benissimo: vi aspettiamo in showroom alle 10." }],
        punta: "[data-testid=filo-testo]",
        premi: false,
      },
      { testo: "Premi la freccia per mandarla.", punta: "[data-testid=filo-invia]", premi: false },
      { parte: "Se la finestra è chiusa", testo: "Dopo 24 ore dall'ultimo messaggio del cliente, al posto del campo compare «Finestra chiusa»: WhatsApp lascia mandare solo un messaggio già approvato. Quando il cliente risponde, la finestra si riapre." },
      { testo: "Se vedi «Manda un modello», premilo: si apre «Riprendi la conversazione». Controlla il nome di chi lo riceve e premi «Manda questo messaggio». Il testo, che leggi sotto «Così lo legge», è quello approvato da WhatsApp e non si cambia: parla col tuo nome e arriva al cliente dal numero dello showroom." },
    ],
    dopo: "Il messaggio parte dal numero dello showroom. In fondo alla bolla vedi l'ora e se è arrivato («recapitato») o se l'ha letto («letto»).",
    seNonVa: "Se al posto del campo c'è «Finestra chiusa» e «Manda un modello» non c'è, WhatsApp non lascia scrivere finché non scrive lui: chiamalo. Il modello dalle 21 alle 9 aspetta le 9, e per ogni conversazione se ne manda uno solo ogni 24 ore. Se una conversazione non la vedi, il titolare non te l'ha ancora affidata.",
  },
  {
    id: "cliente-da-whatsapp",
    gruppo: "messaggi",
    titolo: "Aggiungere come cliente chi ti scrive su WhatsApp",
    quando: "Ti serve quando scrive un numero che non è ancora in archivio, per esempio dopo aver visto la vetrina o una pubblicità.",
    voce: "messaggi",
    ruoli: ["titolare", "venditore"],
    vai: { label: "Apri i messaggi", to: "/messaggi" },
    foto: { come: "venditore", da: "/messaggi", prepara: "conversazione-nuova" },
    passi: [
      { testo: "Apri «Messaggi» e premi la conversazione. Se il numero non è in archivio, c'è scritto «Non è in archivio».", punta: "[data-testid=conversazione-393330907788]", poi: "[data-testid=filo-aggiungi-cliente]" },
      { testo: "Premi «Aggiungi come cliente». Si apre «Nuovo cliente» con il numero già scritto e WhatsApp fra le provenienze.", punta: "[data-testid=filo-aggiungi-cliente]", poi: "[data-testid=nuovo-cliente-nome]" },
      {
        testo: "Controlla il nome (aggiungi il cognome, se lo sai) e il numero.",
        prima: [
          { riempi: "[data-testid=nuovo-cliente-nome]", con: "Elena Sartori" },
          { riempi: "[data-testid=nuovo-cliente-telefono]", con: "333 090 7788" },
        ],
        punta: "[data-testid=nuovo-cliente-nome] >> xpath=../..",
        premi: false,
      },
      { testo: "Scegli la fase e, se sai già quando viene, il giorno e l'orario, come in «Aggiungere un nuovo cliente». Poi premi «Salva». Se hai scelto il giorno, il pulsante dice «Salva e fissa la visita»: al cliente parte la conferma su WhatsApp.", punta: "[data-testid=nuovo-cliente-salva]", poi: "text=Cliente aggiunto" },
    ],
    dopo: "La conversazione si lega al cliente nuovo: in «Messaggi» a destra ne vedi la fase e «Apri la scheda».",
    seNonVa: "Se prima di salvare compare l'avviso che il numero è già in archivio, quel cliente c'è già: premi «Apri la sua scheda» e non crearne un altro. Per legare la conversazione a lui, scegli il cliente nel campo «Aggancia a un cliente» (sul telefono si chiama «Oppure collegalo a un cliente» e sta sotto «Gestisci»).",
  },

  // ── I numeri ──────────────────────────────────────────────────────────────
  {
    id: "dashboard",
    gruppo: "numeri",
    titolo: "Leggere la Dashboard",
    quando: "Ti serve per capire in un minuto come va il mese e cosa è fermo.",
    voce: "cruscotto",
    vai: { label: "Apri la Dashboard", to: "/cruscotto" },
    foto: { come: "titolare", da: "/cruscotto" },
    passi: [
      { testo: "In alto scegli il periodo: questo mese, il mese scorso, gli ultimi tre mesi o da inizio anno.", punta: "[data-testid=cruscotto-periodo]", premi: false },
      { testo: "«Quanto abbiamo venduto» conta i contratti firmati nel periodo, il netto e quanto resta da incassare. La barra dice quanto manca al milione. Nelle foto i numeri sono sfocati: sul tuo schermo li vedi.", punta: "[data-testid=cruscotto-testata]", premi: false },
      { testo: "«Chi sta arrivando»: i contatti nuovi, da dove arrivano di più e gli appuntamenti dei prossimi 7 giorni.", punta: "[data-testid=cruscotto-arrivi]", premi: false },
      { testo: "«Cosa è fermo»: i preventivi senza risposta da più di 7 giorni, i link che stanno per scadere, le assistenze in ritardo.", punta: "[data-testid=cruscotto-cosa-fermo]", premi: false },
      { testo: "«Da riprendere»: i preventivi fermi più grandi. Premi una riga per aprire il preventivo e richiamare il cliente.", punta: "[data-testid=cruscotto-fermi]", premi: false },
    ],
    dopo: "Il venduto conta solo i contratti firmati: un preventivo confermato ma non ancora firmato non entra.",
    seNonVa: "Se compare «I numeri non sono arrivati», premi «Riprova».",
  },
  {
    id: "fatture-incassi",
    gruppo: "numeri",
    titolo: "Fatture e incassi: chi deve pagare",
    quando: "Ti serve per vedere quanto è entrato, quanto deve ancora arrivare e chi è in ritardo.",
    voce: "fatture-incassi",
    vai: { label: "Apri Fatture e incassi", to: "/fatture-incassi" },
    foto: { come: "amministrazione", da: "/fatture-incassi", prepara: "contratto-firmato" },
    passi: [
      {
        testo: "In alto, quattro riquadri: quanto è entrato questo mese, quanto resta da incassare, quanto è in ritardo e quanto scade entro 10 giorni. Nelle foto i numeri sono sfocati: sul tuo schermo li vedi.",
        punta: "[data-testid=cifra-incassato] >> xpath=ancestor::*[.//*[@data-testid='cifra-in-ritardo']][1]",
        premi: false,
      },
      { testo: "In «Chi deve pagare» trovi ogni rata con la sua scadenza, prima chi è in ritardo. Con «Tutte», «In ritardo» e «Entro 10 giorni» scegli quali vedere; «Mostra tutte» apre l'elenco intero. Quando arriva un pagamento, premi «Segna incasso».", punta: RATA_CHIARA, premi: false },
      { testo: "Nella scheda «Fatture» prepari la fattura delle rate.", punta: "[data-testid=scheda-fatture]" },
      { testo: "Premi «Prepara la fattura» accanto alla rata.", punta: "[data-testid^=prepara-]", premi: false },
    ],
    dopo: "Preparata la fattura, compare «Invia»: la manda a Fatture in Cloud, se il collegamento è acceso in «Impostazioni».",
    seNonVa: "Se una rata non c'è, il contratto non è ancora segnato firmato.",
  },

  // ── Solo per il titolare ──────────────────────────────────────────────────
  {
    id: "collaboratore",
    gruppo: "titolare",
    titolo: "Aggiungere un collaboratore e decidere cosa vede",
    quando: "Ti serve quando entra una persona nuova nel team, o quando vuoi cambiare cosa vede qualcuno.",
    voce: "impostazioni",
    ruoli: ["titolare"],
    vai: { label: "Apri le impostazioni", to: "/impostazioni" },
    foto: { come: "titolare", da: "/impostazioni" },
    passi: [
      { parte: "Invitare", testo: "Apri «Impostazioni» e, nel gruppo «Persone», premi «Collaboratori e permessi». Sul computer le voci sono nel menu a sinistra; sul telefono sono l'elenco dei gruppi.", punta: "[data-testid=tab-utenti]", poi: "[data-testid=btn-invite-user]" },
      { testo: "Premi «Invita una persona».", punta: "[data-testid=btn-invite-user]", poi: "[data-testid=invite-user-modal]" },
      {
        testo: "Scrivi nome, cognome ed email, poi scegli il ruolo e la sede.",
        prima: [
          { riempi: "[data-testid=input-user-nome]", con: "Laura" },
          { riempi: "[data-testid=input-user-cognome]", con: "Conti" },
          { riempi: "[data-testid=input-user-email]", con: "laura.conti@internidautore.com" },
        ],
        punta: "[data-testid=input-user-nome] >> xpath=ancestor::*[.//*[@data-testid='select-user-sede']][1]",
        premi: false,
      },
      { testo: "Quando hai scelto ruolo e sede, premi «Invia invito»: resta grigio finché mancano. Alla persona arriva un'email per scegliere la password.", punta: "[data-testid=submit-invite-user]", premi: false },
      {
        parte: "Cosa vede",
        testo: "Per decidere cosa vede una persona, premi la sua riga.",
        prima: [{ tasti: ["Escape"] }],
        punta: "[data-testid=users-row-{utente:venditore}]",
        poi: "[data-testid=permessi-pannello]",
      },
      {
        testo: "Scegli le sedi che vede e accendi o spegni le sezioni del suo menu.",
        punta: "[data-testid=permessi-sezione-clienti] >> xpath=ancestor::*[.//*[@data-testid='permessi-sezione-messaggi']][1]",
        premi: false,
      },
      { testo: "Premi «Salva i permessi».", punta: "[data-testid=permessi-salva]", premi: false },
    ],
    dopo: "I permessi valgono subito sui dati; il menu di quella persona cambia quando ricarica la pagina.",
    seNonVa: "L'invito vale 24 ore: se scade, apri la sua riga e premi «Rimanda l'invito». Se dice che la persona ha già accettato, cambia i permessi dalla sua riga.",
  },
  {
    id: "prezzi",
    gruppo: "titolare",
    titolo: "Cambiare ricarichi, sconti e livello minimo",
    quando: "Ti serve quando cambiano i tuoi margini o gli sconti del fornitore.",
    voce: "impostazioni",
    ruoli: ["titolare"],
    vai: { label: "Apri le impostazioni", to: "/impostazioni" },
    foto: { come: "titolare", da: "/impostazioni" },
    passi: [
      { testo: "In «Impostazioni», nel gruppo «Preventivi», premi «Prezzi».", punta: "[data-testid=tab-prezzi]", poi: "[data-testid=prezzi-livello-M5]" },
      {
        testo: "«Livelli di ricarico»: il ricarico di ogni livello, da M5 (il più alto) a M1. Cambi un numero e premi «Salva i livelli». Nelle foto i numeri sono sfocati: sul tuo schermo li vedi.",
        punta: "[data-testid=prezzi-livello-M5] >> xpath=ancestor::*[.//*[@data-testid='prezzi-salva-livelli']][1]",
        premi: false,
      },
      { testo: "«Sconti d'acquisto»: gli sconti del fornitore per categoria, uno dopo l'altro, per esempio «10 + 5». Poi «Salva gli sconti».", punta: "[data-testid=prezzi-catena-mobilio]", premi: false },
      { testo: "«Livello minimo per ruolo»: sotto questo livello chi vende non scende nel calcolatore. Lo cambi con un tocco e confermi.", punta: "[data-testid=prezzi-soglia-venditore]", premi: false },
    ],
    dopo: "Il calcolatore usa i numeri nuovi dal calcolo successivo. Ogni modifica resta scritta nel «Registro delle modifiche», che trovi nel gruppo «Controllo».",
    seNonVa: "Se dice per esempio «M4 deve stare sopra M3», i livelli vanno in ordine: a ogni livello più alto, un ricarico più grande.",
  },
];

/** Le guide che una persona vede: quelle dei gesti che può fare. `voci` = vociDi(ruolo, spente). */
export function guideDi(ruolo, voci) {
  return GUIDE.filter((g) => (!g.voce || voci.includes(g.voce)) && (!g.ruoli || g.ruoli.includes(ruolo)));
}

const piano = (s) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[’‘]/g, "'").toLowerCase();

/** La ricerca: ogni parola scritta deve stare nel titolo, nel «quando» o nei passi. Accenti e maiuscole non contano. */
export function cerca(guide, q) {
  const parole = piano(q).split(/\s+/).filter(Boolean);
  if (!parole.length) return guide;
  return guide.filter((g) => {
    const t = piano([g.titolo, g.quando, ...g.passi.map((p) => p.testo)].join(" "));
    return parole.every((p) => t.includes(p));
  });
}
